import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";

import Input from "../components/Input";
import Loading from "../components/Loading";
import RolePermissionChecklist from "../components/RolePermissionChecklist";
import { getRole } from "../actions/AdminAction";
import { getRoleByID, updateRole } from "../actions/roleActions";
import {
  GET_ROLE_BY_ID_RESET,
  UPDATE_ROLE_RESET,
} from "../constants/roleConstants";
import { emptyPermissions, hasPermission, pickPermissions } from "../constants/rolePermissions";
import ScreenHeader from '../components/layout/ScreenHeader'

const RoleByID = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // * States
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState(emptyPermissions);
  const saveTimeoutRef = useRef(null);

  // * Initialization
  const dispatch = useDispatch();

  const { userInfo: adminInfo } = useSelector((state) => state.adminLogin);
  const { userInfo } = useSelector((state) => state.userLogin);
  const currentRole = adminInfo?.role || userInfo?.role;
  const isSuperAdmin = currentRole?.isSuperAdmin === true;

  // * Get Role By ID
  const getRoleByIDInfo = useSelector((state) => state.getRoleByIDInfo);
  const { loadingGetRoleByID, getRoleByIDData, errorGetRoleByID } =
    getRoleByIDInfo;

  // Only a Super Admin may edit a Super Admin role (backend enforces the same)
  const canUpdateRoles =
    hasPermission(currentRole, "rolesUpdate") &&
    (isSuperAdmin || getRoleByIDData?.isSuperAdmin !== true);

  useEffect(() => {
    dispatch({ type: GET_ROLE_BY_ID_RESET });
    dispatch(getRoleByID(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (getRoleByIDData) {
      setName(getRoleByIDData.name || "");
      setPermissions(pickPermissions(getRoleByIDData));
    } else if (errorGetRoleByID) {
      toast(errorGetRoleByID, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
    }
  }, [getRoleByIDData, errorGetRoleByID]);

  // * Auto-save each change (debounced so quick clicks become one request)
  const handlePermissionsChange = (nextPermissions) => {
    if (!canUpdateRoles) return;
    setPermissions(nextPermissions);
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      dispatch(updateRole(id, nextPermissions));
    }, 300);
  };

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  const updateRoleInfo = useSelector((state) => state.updateRoleInfo);
  const { loadingUpdateRoleInfo, errorUpdateRoleInfo, updateRoleData } =
    updateRoleInfo;

  useEffect(() => {
    if (updateRoleData) {
      dispatch({ type: UPDATE_ROLE_RESET });
      toast("Permission saved", {
        type: "success",
        hideProgressBar: true,
        autoClose: 1000,
      });
      // Refresh the current user's role data so sidebar updates immediately
      dispatch(getRole());
    } else if (errorUpdateRoleInfo) {
      dispatch({ type: UPDATE_ROLE_RESET });
      toast(errorUpdateRoleInfo, {
        type: "error",
        hideProgressBar: true,
        autoClose: 2000,
      });
      // Show what is actually saved after a rejected change
      dispatch(getRoleByID(id));
    }
  }, [updateRoleData, errorUpdateRoleInfo, dispatch, id]);

  if (loadingGetRoleByID) {
    return <Loading />;
  }

  return (
    <div className="w-full h-full">
      <div className="flex items-center justify-between gap-3">
        <ScreenHeader title="Role details" description="Change what people with this role can see and do." className="!mb-0" />
        <button
          type="button"
          onClick={() => navigate("/roles")}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <ArrowLeft size={18} strokeWidth={2.2} />
          Back to Roles
        </button>
      </div>
      <div className="bg-white shadow-md rounded px-8 py-8 my-4">
        <h3 className="text-md font-semibold mb-2">Name</h3>
        <Input
          name="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={true}
        />

        <RolePermissionChecklist
          permissions={permissions}
          onChange={handlePermissionsChange}
          disabled={!canUpdateRoles}
          canGrantSuperAdmin={isSuperAdmin}
        />

        {canUpdateRoles ? (
          <div className="mt-6 flex items-center gap-2">
            {loadingUpdateRoleInfo ? (
              <span className="text-blue-600 text-sm font-medium">
                Saving...
              </span>
            ) : (
              <span className="text-green-600 text-sm font-medium">
                ✓ All changes auto-saved
              </span>
            )}
          </div>
        ) : (
          <p className="mt-6 text-gray-500 italic">
            You have view-only access to this role.
          </p>
        )}
      </div>
    </div>
  );
};

export default RoleByID;
